
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Table, Tag, Pagination } from 'antd';
import { StaffApplicantSearchContainerApplicantUserSearchFieldsFragment } from '../../../../generated';
import { FormatDate } from '../../../../constants';

interface PaginationComponentProps {
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number, size?: number) => void;
}

const PaginationComponent: React.FC<PaginationComponentProps> = (props) => {
  return (
    <Pagination
      current={props.current}
      pageSize={props.pageSize}
      total={props.total}
      showTotal={(total) => `Count: ${total} applicants`}
      onChange={props.onChange}
      onShowSizeChange={props.onChange}
      className="rounded-[10px] p-[15px] border border-[#f0f0f0] my-3"
    />
  );
};

interface ApplicantSearchTableProps {
  resultCount: number;
  data: StaffApplicantSearchContainerApplicantUserSearchFieldsFragment[];
}

export const ApplicantSearchTable: React.FC<ApplicantSearchTableProps> = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const handlePaginationChange = (page: number, size?: number) => {
    const newSize = size || pageSize;
    setCurrentPage(page);
    setPageSize(newSize);
    searchParams.set('page', page.toString());
    searchParams.set('pageSize', newSize.toString());
    setSearchParams(searchParams);
  };

  const columns = [
    {
      title: 'Applicant Id',
      dataIndex: 'id',
      key: 'id',
      render: (text: string, record: any) => <Link to={`/staff/applicants/${record.id}/applicant-details`}>{text}</Link>
    },
    {
      title: 'Last Name',
      dataIndex: ['identityDetails', 'lastName'],
      key: 'lastName',
      render: (text: string) => <div className="break-words max-w-[200px]">{text}</div>
    },
    {
      title: 'Rest of Name',
      dataIndex: ['identityDetails', 'restOfName'],
      key: 'restOfName',
      render: (text: string) => <div className="break-words max-w-[200px]">{text}</div>
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => (text ? FormatDate(text) : '')
    },
    {
      title: 'Restricted',
      dataIndex: 'accessBlocked',
      key: 'accessBlocked',
      render: (text: boolean) => (text ? 'Yes' : 'No')
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags: string[]) =>
        tags.map((tag) => (
          <Tag className="border border-black" key={tag}>
            {tag}
          </Tag>
        ))
    }
  ];

  useEffect(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    const size = parseInt(searchParams.get('pageSize') || '10', 10);

    setCurrentPage(page);
    setPageSize(size);
  }, [searchParams]);

  return (
    <div className="flex flex-col w-full pt-[2px]">
      <PaginationComponent current={currentPage} pageSize={pageSize} total={props.resultCount} onChange={handlePaginationChange} />
      <Table
        className="w-full border border-[#f0f0f0] rounded-[10px] p-[15px] max-w-full border-solid"
        columns={columns}
        dataSource={props.data}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={false}
        rowHoverable={false}
      />
      <PaginationComponent current={currentPage} pageSize={pageSize} total={props.resultCount} onChange={handlePaginationChange} />
    </div>
  );
};
